// LibraryDrawer -- the "Slide library" for the deck builder, a NEAR-FULL-SCREEN
// categorized GRID gallery (Lane 315 / deck v3 amendment 3, 2026-07-22). A dim
// overlay + a centered panel that holds the search, a category-grouped
// responsive GRID of clear slide cards (title + Editable/Generated tag + clamped
// summary + a per-card "When to use" advisory expand + Add), and a footer with
// the deck count + Done. Replaces the old narrow left-sliding single-scroll
// drawer -- the single-column list was too much to scan (amendment 3).
//
// Presentational: all deck state + the add action live in the DeckAssembler
// parent (single source of truth); this owns only its local search text, which
// advisory card is expanded, and a background-scroll lock while open. Calls
// onAdd(template_key) to append a slide. The prop contract is UNCHANGED from the
// drawer (open / onClose / templates / onAdd / inDeckCounts / slideCount /
// subjectName) -- this is a display-only reshape.
//
// Registry-driven (Rule 116): templates come from /api/decks/templates verbatim;
// grouping uses the registry `category` field; the Editable/Generated tag is
// derived ONLY from each template's editable_fields (no backend capability added).
// No hardcoded template_key. No emojis (design-system rule); SVG/text + tokens only.
//
// Lane 145 (Track A / A1): each card gains a "When to use" expand rendering the
// template's static `advisory` record (use_when / avoid_when / what to say / the
// ask / what it shares) + the advocacy include/leave-out rule -- human-authored
// slide semantics from helpers/deck_advisory.py, surfaced verbatim (never
// generated per supplier). A template without an advisory record renders exactly
// as before (no toggle). In the grid, expanding a card spans it to the full row
// so the advisory has room; only one card expands at a time (keeps it scannable).
//
// Lane 299 (QA-8): the Linear `.dk` class layer (DeckAssembler.linear.css).
//
// Add stays ENABLED even when a slide is already in the deck -- the builder
// supports duplicate slides (per-instance edits, Part 5), so disabling Add would
// REMOVE that capability. The added state is shown via an "in deck (N)" chip + an
// "Add again" label instead of a disabled button.

import React, { useState, useMemo, useEffect } from 'react';

function groupByCategory(templates) {
  // Stable, registry-order grouping: the endpoint already ORDER BY category, name
  // (deck_compose.list_active_templates), so we preserve first-seen order.
  const groups = [];
  const index = new Map();
  for (const t of templates) {
    const cat = t.category || 'Other';
    if (!index.has(cat)) {
      index.set(cat, groups.length);
      groups.push({ category: cat, items: [] });
    }
    groups[index.get(cat)].items.push(t);
  }
  return groups;
}


function CloseIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
      <path d="M3 3 L13 13 M13 3 L3 13" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

function Chevron({ open }) {
  return (
    <span className={`dk-chv${open ? ' open' : ''}`}>
      <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true" focusable="false">
        <path d="M3 1.5 L7 5 L3 8.5" fill="none" stroke="currentColor" strokeWidth="1.5"
          strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </span>
  );
}

// One labeled advisory line. Renders nothing for an empty value so a partial
// record never shows a bare label.
function AdvisoryRow({ label, text }) {
  if (!text) return null;
  return (
    <div className="dk-adv-row">
      <span className="ak">{label}</span>
      <span className="av">{text}</span>
    </div>
  );
}

// The expanded advisory block for one template card. Verbatim registry content
// (helpers/deck_advisory.py via the templates endpoint) -- static, authored
// once, never per-supplier (Rule 116: nothing here is generated).
function AdvisoryDetails({ advisory }) {
  const adv = advisory.advocacy || {};
  return (
    <div className="dk-adv-body">
      <AdvisoryRow label="Use when" text={advisory.use_when} />
      <AdvisoryRow label="Avoid when" text={advisory.avoid_when} />
      <AdvisoryRow label="What to say" text={advisory.talk_track} />
      <AdvisoryRow label="The ask" text={advisory.follow_on_ask} />
      <AdvisoryRow label="What it shares" text={advisory.supplier_facing_risk} />
      <AdvisoryRow label="Included when" text={adv.include_when} />
      <AdvisoryRow label="Left out when" text={adv.exclude_when} />
    </div>
  );
}

// One slide card in the grid. Kept as a small inline component so the grid map
// stays readable; still stateless (expand/add handled by the parent).
function SlideCard({ template, count, verdict, advisoryOpen, onToggleAdvisory, onAdd }) {
  const t = template;
  const isEditable = Array.isArray(t.editable_fields) && t.editable_fields.length > 0;
  const hasAdvisory = !!(t.advisory && (t.advisory.use_when || t.advisory.avoid_when));
  const expanded = hasAdvisory && advisoryOpen;
  // ⭐ LANE 941 -- THIS BUILD'S VERDICT ON THIS PAGE, BESIDE ITS ADD BUTTON.
  //
  // Brad opened this library, found a page the build had excluded, and added it
  // with nothing on screen saying so. Adding it is still allowed and always will
  // be -- Ryan, 2026-08-08: every page lives in the library and the buyer may add
  // anything he wants. What changes is that he now does it KNOWINGLY.
  //
  // ⛔ THIS DOES NOT DISABLE `Add`, AND MUST NOT. There is no such thing as a page
  // the buyer may not have; "excluded by default, with its reason shown" is the
  // whole design. A disabled Add here would be a different product.
  //
  // Only `withheld` earns a chip. `available`, `in_deck` and `scaffold` say
  // nothing new -- the in-deck pill above already covers one, and printing
  // "available" on 28 cards is noise that would train the eye to skip the row the
  // one time it matters.
  const withheld = verdict && verdict.state === 'withheld';
  return (
    <div className={`dk-libcard${expanded ? ' exp' : ''}${withheld ? ' wh' : ''}`}
      title={t.purpose || undefined}>
      <div className="dk-libcard-hd">
        <span className="nm">{t.name}</span>
      </div>
      <div className="dk-libcard-pills">
        <span className={isEditable ? 'dk-pill edit' : 'dk-pill gen'}>
          {isEditable ? 'Editable' : 'Generated'}
        </span>
        {count > 0 && (
          // Slides STAY addable (duplicate-add is a feature); this only signals
          // it is already in the deck, with a count when added more than once.
          <span className="dk-pill indeck">
            {`in deck${count > 1 ? ` (${count})` : ''}`}
          </span>
        )}
        {withheld && count === 0 && (
          // Suppressed when the page IS in the deck: he has already put it back,
          // so "left out" would contradict the pill sitting next to it.
          <span className="dk-pill wh" data-testid={`verdict-${t.template_key}`}>
            left out of this deck
          </span>
        )}
      </div>
      {t.purpose && (
        // Compact: clamp the summary to ~3 lines; full text on hover via the
        // card's title tooltip (touch keeps the truncated lines visible).
        <div className="dk-libcard-tp dk-clamp3">{t.purpose}</div>
      )}
      {withheld && verdict.reason && (
        // The gate's OWN sentence, verbatim. Nothing is authored here.
        <div className="dk-libcard-wh" data-testid={`verdict-reason-${t.template_key}`}>
          {verdict.reason}
        </div>
      )}
      {/* ⚠️ `typeof === 'string'` IS NOT DEFENSIVE PADDING -- IT WAS CAUGHT BY A
          FAILING TEST, and the failure was not a wrong string, it was a WHITE
          SCREEN. `page_verdicts` is persisted JSONB, so a record written by an
          older or a future producer can carry any shape; a truthy non-string
          `sentence` reaches React as a child and React THROWS ("Objects are not
          valid as a React child"), which unmounts the whole assembler -- and this
          drawer is always mounted, so the deck goes with it. A `figure &&
          figure.sentence` truthiness check is exactly what fails here, which is
          why the rail's own memo does the same typeof test. */}
      {withheld && verdict.figure
        && typeof verdict.figure.sentence === 'string' && verdict.figure.sentence && (
        // ⭐ LANE 948 -- BEAT 1, THE FIGURE THE DECISION WAS MADE ON, right where
        // Brad added a page blind. It is the SAME string the build rail shows for
        // this page, composed once in `deck_objectives._register_figure`, so the
        // two surfaces cannot word one decision two ways -- the failure Rule 120
        // is about and the one Lane 941 already had to fix once between these two
        // components. Absent whenever the exclusion has no figure attached.
        <div className="dk-libcard-fg" data-testid={`verdict-figure-${t.template_key}`}>
          {verdict.figure.sentence}
        </div>
      )}
      <div className="dk-libcard-f">
        {hasAdvisory ? (
          <button type="button" className="dk-adv-t"
            aria-expanded={expanded}
            onClick={onToggleAdvisory}>
            <Chevron open={expanded} />
            When to use
          </button>
        ) : <span />}
        <button type="button" className="dk-btn sm" onClick={onAdd}>
          {count > 0 ? 'Add again' : 'Add'}
        </button>
      </div>
      {expanded && <AdvisoryDetails advisory={t.advisory} />}
    </div>
  );
}

// Lane 789: "Generate a page" MOVED HERE from the rail, and this is its natural
// home rather than a saving of one button. The library IS the catalogue of pages
// you can add; generating one is what you do when the catalogue does not have the
// page you want. Every prop below is OPTIONAL and absent by default, so a caller
// that does not pass them renders exactly the library that shipped before.
function LibraryDrawer({
  open, onClose, templates = [], onAdd, inDeckCounts = {}, slideCount = 0, subjectName,
  // Lane 941: `{template_key: {state, reason, hard}}` from the build's own record
  // (intake.intent.page_verdicts). Default `{}` -> no card shows a verdict and
  // this component renders exactly what it rendered before, which is also what
  // every deck built before this lane gets.
  pageVerdicts = {},
}) {
  const [query, setQuery] = useState('');
  // Which card's advisory block is expanded (one at a time keeps the grid scannable).
  const [advisoryKey, setAdvisoryKey] = useState(null);

  // Lock background scroll while the gallery is open; always restore on close/unmount.
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return templates;
    return templates.filter((t) => {
      const hay = [t.name, t.purpose, t.category, t.audience,
        t.advisory && t.advisory.use_when, t.advisory && t.advisory.avoid_when,
        ...(Array.isArray(t.tags) ? t.tags : [])]
        .filter(Boolean).join(' ').toLowerCase();
      return hay.includes(q);
    });
  }, [templates, query]);

  const groups = useMemo(() => groupByCategory(filtered), [filtered]);

  // No `.dk` wrapper: this renders inside DeckAssembler's `.dk` root, so the
  // token layer inherits down to these fixed-position children (CSS custom
  // properties inherit through the DOM tree regardless of positioning). Wrapping
  // in `.dk` here would paint a stray full-viewport background.
  return (
    <>
      {/* Dim overlay -- click to close. Fades; non-interactive when closed. */}
      <div className={`dk-overlay ${open ? 'on' : 'off'}`} onClick={onClose} aria-hidden="true" />
      {/* Near-full-screen gallery panel. */}
      <aside
        className={`dk-lib ${open ? 'on' : 'off'}`}
        role="dialog" aria-modal="true" aria-label="Slide library"
      >
        {/* Header: title + subtitle on the left, search + close on the right. */}
        <div className="dk-lib-h">
          <div className="dk-lib-h-titles">
            <div className="dk-lib-title">Slide library</div>
            <div className="dk-lib-sub">
              {templates.length} pre-built slide{templates.length === 1 ? '' : 's'}
              {subjectName ? ` · pull from ${subjectName}` : ''}
            </div>
          </div>
          <div className="dk-lib-h-tools">
            <div className="dk-lib-search">
              <input className="dk-inp" value={query} placeholder="Search slides..."
                onChange={(e) => setQuery(e.target.value)} aria-label="Search slides" />
            </div>
            <button type="button" className="dk-btn sm" onClick={onClose} aria-label="Close library"
              style={{ lineHeight: 0 }}>
              <CloseIcon />
            </button>
          </div>
        </div>


        {/* Scrollable category grid. */}
        <div className="dk-lib-body">
          {groups.length === 0 && (
            <div className="dk-lib-empty">
              {templates.length === 0 ? 'No slides available.' : 'No slides match your search.'}
            </div>
          )}
          {groups.map((g) => (
            <section key={g.category} className="dk-lib-cat">
              <div className="dk-lib-cat-h">
                <span className="dk-cat-lbl">{g.category}</span>
                <span className="dk-lib-cat-n">{g.items.length}</span>
              </div>
              <div className="dk-lib-grid">
                {g.items.map((t) => {
                  const advisoryOpen = advisoryKey === t.template_key;
                  return (
                    <SlideCard
                      key={t.template_key}
                      template={t}
                      count={inDeckCounts[t.template_key] || 0}
                      verdict={pageVerdicts[t.template_key] || null}
                      advisoryOpen={advisoryOpen}
                      onToggleAdvisory={() => setAdvisoryKey(advisoryOpen ? null : t.template_key)}
                      onAdd={() => onAdd(t.template_key)}
                    />
                  );
                })}
              </div>
            </section>
          ))}
        </div>

        {/* Footer */}
        <div className="dk-lib-f">
          <span className="n">
            {slideCount} slide{slideCount === 1 ? '' : 's'} in your deck
          </span>
          <button type="button" className="dk-btn pri" onClick={onClose}>Done</button>
        </div>
      </aside>
    </>
  );
}

export default LibraryDrawer;
